import { movies, featuredIds } from '@/mocks/movies';
import { categoryGroups, listCategories } from '@/mocks/categories';
import type { CategoryGroup, ListCategory, Movie, MovieType } from '@/lib/movieTypes';

export function getAllMovies(): Movie[] {
  return movies;
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getFeaturedMovies(): Movie[] {
  return featuredIds
    .map((id) => movies.find((m) => m.id === id))
    .filter((m): m is Movie => Boolean(m));
}

export function getMoviesByType(type: MovieType): Movie[] {
  return movies.filter((m) => m.type === type);
}

export function getMoviesByTypeAndSub(type: MovieType, sub: string): Movie[] {
  return movies.filter((m) => m.type === type && m.subCategory === sub);
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, limit);
}

export function getHotMovies(limit = 5): Movie[] {
  return [...movies].sort((a, b) => b.playCount - a.playCount).slice(0, limit);
}

export interface MovieFilter {
  type?: MovieType | '';
  subCategory?: string;
  regions?: string[];
  years?: number[];
  minRating?: number;
  sort?: 'latest' | 'rating' | 'hot' | 'year';
}

export function filterMovies(filter: MovieFilter = {}): Movie[] {
  const { type = '', subCategory = '', regions = [], years = [], minRating = 0, sort = 'latest' } = filter;

  const result = movies.filter((m) => {
    if (type && m.type !== type) return false;
    if (subCategory && m.subCategory !== subCategory) return false;
    if (regions.length > 0 && !regions.includes(m.region)) return false;
    if (years.length > 0 && !years.includes(m.year)) return false;
    if (minRating > 0 && m.rating < minRating) return false;
    return true;
  });

  return sortMovies(result, sort);
}

export function sortMovies(list: Movie[], sort: MovieFilter['sort']): Movie[] {
  const sorted = [...list];
  switch (sort) {
    case 'rating':
      return sorted.sort((a, b) => b.rating - a.rating);
    case 'hot':
      return sorted.sort((a, b) => b.playCount - a.playCount);
    case 'year':
      return sorted.sort((a, b) => b.year - a.year);
    case 'latest':
    default:
      return sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }
}

export function searchMovies(keyword: string): Movie[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return movies.filter((m) => {
    const haystack = [
      m.title,
      m.typeName,
      m.subCategoryName,
      m.region,
      m.director,
      m.summary,
      ...m.cast,
      ...m.tags,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const scored = movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      let score = 0;
      if (m.type === movie.type) score += 2;
      if (m.subCategory === movie.subCategory) score += 3;
      if (m.director === movie.director) score += 4;
      score += m.tags.filter((t) => movie.tags.includes(t)).length * 2;
      return { m, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || b.m.rating - a.m.rating);

  return scored.slice(0, limit).map((item) => item.m);
}

export function getCategoryGroupBySlug(slug: string): CategoryGroup | undefined {
  return categoryGroups.find((g) => g.slug === slug || g.type === slug);
}

export function getListCategoryById(id: string): ListCategory | undefined {
  return listCategories.find((c) => c.id === id);
}

export function getListCategoriesByType(type: MovieType): ListCategory[] {
  return listCategories.filter((c) => c.type === type);
}

export function formatCount(count: number): string {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)} 万`;
  }
  return String(count);
}